import { childIsRequired } from "../../../Common/FormBuilder/formFunctions";

export const checkSegmentStatus = (
    segment,
    formStatus = null,
    isAppraiser = false
) => {
    const elements = segment?.form?.elements ?? [];
    const status = segment?.status ?? "open";

    if (status === "completed") {
        return "success";
    }

    switch (formStatus) {
        case "":
        case null:
            return "";

        case "completed":
            return "success";

        case "awaiting_approval":
        case "awaiting_approval_1":
        case "awaiting_approval_2":
            if (!isAppraiser) {
                return "info";
            }
            break;

        default:
            break;
    }

    if (segment?.isRequired) {
        let complete = elements.every(
            ({ isRequired, response, children }) =>
                (!isRequired || !!response) &&
                (!children ||
                    children.every(
                        (child) =>
                            !!child.response ||
                            (!child.isRequired &&
                                !childIsRequired(
                                    child.isRequiredCondition,
                                    response
                                ))
                    ))
        );
        if (complete && segment.type === "activities") {
            complete = segment.items.every(
                ({ review }) =>
                    !review ||
                    !review.isRequired ||
                    review.status === "completed" ||
                    review.status === "n/a"
            );
        }
        return complete ? "success" : "warning";
    } else {
        return elements.length > 0 ? "disabled" : "";
    }
};

export const formStatusMessage = (
    form,
    status,
    isAppraiser = false,
    helperText = ""
) => {
    let formStatus = status?.length > 0 ? status : "no_data";

    const messages = {
        inputs: {
            no_data: {
                severity: "info",
                message: helperText ?? "No input data available",
            },
            not_started: {
                severity: isAppraiser ? "info" : "warning",
                message: isAppraiser ? "Not started" : "Start input process",
            },
            completed: { severity: "success", message: "Completed" },
            awaiting_approval: {
                severity: isAppraiser ? "warning" : "info",
                message: `${form} awaiting approval from appraiser`,
            },
            awaiting_approval_1: {
                severity: isAppraiser ? "warning" : "info",
                message: `${form} awaiting approval from secondary appraiser`,
            },
            awaiting_approval_2: {
                severity: isAppraiser ? "info" : "warning",
                message: `${form} awaiting approval`,
            },
            rejected: {
                severity: isAppraiser ? "info" : "warning",
                message: `${form} rejected by appraiser`,
            },
            rejected_1: {
                severity: isAppraiser ? "info" : "warning",
                message: `${form} rejected by secondary appraiser`,
            },
            rejected_2: {
                severity: isAppraiser ? "warning" : "info",
                message: `${form} rejected by appraisee`,
            },
            escalated: { severity: "error", message: "Escalated" },
            incomplete: { severity: "info", message: "In progress" },
        },
        outputs: {
            no_data: {
                severity: "info",
                message: helperText ?? "No output data available",
            },
            not_started: {
                severity: isAppraiser ? "warning" : "info",
                message: isAppraiser ? "Start output process" : "Not started",
            },
            incomplete: {
                severity: isAppraiser ? "warning" : "info",
                message: helperText ?? "In progress",
            },
            awaiting_approval: {
                severity: isAppraiser ? "info" : "warning",
                message: `${form} awaiting approval`,
            },
            awaiting_approval_1: {
                severity: isAppraiser ? "warning" : "info",
                message: `${form} awaiting approval from secondary appraiser`,
            },
            awaiting_approval_2: {
                severity: isAppraiser ? "info" : "warning",
                message: `${form} awaiting your approval`,
            },
            rejected: {
                severity: isAppraiser ? "warning" : "info",
                message: `${form} rejected by appraisee`,
            },
            rejected_1: {
                severity: isAppraiser ? "info" : "warning",
                message: `${form} rejected by secondary appraiser`,
            },
            rejected_2: {
                severity: isAppraiser ? "warning" : "info",
                message: `${form} rejected by appraisee`,
            },
            escalated: { severity: "error", message: "Escalated" },
            completed: { severity: "success", message: "Completed" },
        },

        survey: {
            no_data: {
                severity: "info",
                message: "No survey data available",
            },
            open: {
                severity: isAppraiser ? "info" : "warning",
                message: "Survey to be completed",
            },
            completed: { severity: "success", message: "Completed" },
        },
    };

    return messages[form]?.[formStatus] || { severity: "", message: "" };
};

export const getAppraisalAvatarBadge = (status, isAppraiser = false) => {
    let result = "";

    switch (status) {
        case "review":
            result = isAppraiser ? "review_self" : "review";
            break;
        case "closed":
            result = "error";
            break;
        case "complete":
            result = "checked";
            break;
        default:
            result = "";
            break;
    }

    return result;
};

export const appraisalStatusMessage = (
    status,
    inputStatus,
    meetingStatus,
    outputStatus,
    surveyStatus = null,
    isAppraiser = false
) => {
    let result = {};

    switch (status) {
        case "open":
            result = formStatusMessage("inputs", inputStatus, isAppraiser);
            break;

        case "review":
            result = { severity: "info", message: "Under review" };

            if (inputStatus === "completed") {
                if (outputStatus === "completed") {
                    result = formStatusMessage(
                        "survey",
                        surveyStatus,
                        isAppraiser
                    );
                } else {
                    result = formStatusMessage(
                        "outputs",
                        outputStatus,
                        isAppraiser
                    );
                }
            } else {
                result = formStatusMessage("inputs", inputStatus, isAppraiser);
            }

            break;

        case "closed":
            result = { severity: "error", message: "Closed by Appraiser" };
            break;

        default:
            break;
    }

    return result;
};
